import FormInput from "../../components/forms/FormInput";
import PhoneInput from "../../components/forms/PhoneInput";
import MailboxConnectButtons from "../../components/MailboxConnectButtons";
import ProfileIdentityCard from "./components/ProfileIdentityCard";
import FullScreenLoader from "../../components/FullScreenLoader";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { useForm, FormProvider } from "react-hook-form";
import {
  useGetUserProfileQuery,
  useUpdateSettingsMutation,
} from "../../features/users/usersApi";
import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import Signature from "../../components/Signature";
import { Card, Col, Row } from "react-bootstrap";
import { GoogleOAuthProvider } from "@react-oauth/google";
import PrimarySmallButton from "../../components/buttons/PrimarySmallButton";
import { IoSaveOutline } from "react-icons/io5";
import {
  buildDisconnectedMailboxSignature,
  isMailboxConnected,
  resolveSignatureForMailboxState,
  resolveUserFullName,
} from "../../utils/signatureHelpers";

const googleClientId = import.meta.env.VITE_GOOGLE_ID as string;

const ProfileContent = () => {
  const { data: userProfile, refetch, isFetching } = useGetUserProfileQuery(
    undefined,
    { refetchOnMountOrArgChange: true }
  );

  const [, setProfilePicture] = useState<File | null>(null);

  const [updateSettions, { isLoading: isSaving }] = useUpdateSettingsMutation();

  const schema = yup
    .object({
      first_name: yup.string().required(),
      last_name: yup.string().required(),
      mobile_number: yup.string().optional(),
      country_code: yup.string().optional(),
      email: yup.string().email().required(),
      signature: yup.string().required(),
      service_description: yup.string().optional(),
      company_name: yup.string().optional(),
      state: yup
        .object({
          label: yup.string(),
          value: yup.string(),
        })
        .optional(),
      country: yup
        .object({
          label: yup.string(),
          value: yup.string(),
          id: yup.number(),
        })
        .optional(),
    })
    .required();

  const methods = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      first_name: "",
      last_name: "",
      email: "",
      signature: "",
      service_description: "",
      company_name: "",
      country_code: "+1",
    },
  });

  useEffect(() => {
    if (userProfile?.data) {
      const profile = userProfile.data;
      const fullName = resolveUserFullName(
        profile.first_name,
        profile.last_name,
        profile.full_name
      );
      const signature = resolveSignatureForMailboxState(
        profile.mailbox_type,
        fullName,
        profile.signature,
        profile.scope
      );

      methods.reset({
        first_name: profile.first_name,
        last_name: profile.last_name,
        mobile_number: profile.mobile_number,
        country_code: profile.country_code || "+1",
        email: profile.email,
        signature,
        service_description: profile.service_description,
        company_name: profile?.organization?.name,
      });
    }
  }, [userProfile, methods]);

  const {
    register,
    handleSubmit,
    formState: { errors, isDirty },
    setValue,
    watch,
  } = methods;

  const mobileNumber = watch("mobile_number");
  const firstName = watch("first_name");
  const lastName = watch("last_name");
  const watchedSignature = watch("signature");
  const user = userProfile?.data;
  const mailboxConnected = isMailboxConnected(user?.mailbox_type, user?.scope);
  const displayFullName = resolveUserFullName(
    firstName,
    lastName,
    user?.full_name
  );
  const lockedSignature = buildDisconnectedMailboxSignature(displayFullName);
  const signatureForDisplay = mailboxConnected
    ? watchedSignature
    : lockedSignature;

  useEffect(() => {
    if (!user || mailboxConnected) return;

    setValue("signature", lockedSignature, {
      shouldValidate: true,
    });
  }, [user, mailboxConnected, lockedSignature, setValue]);

  const onSubmit = async (data: {
    first_name: string;
    last_name: string;
    mobile_number?: string;
    signature: string;
    service_description?: string;
  }) => {
    const fullName = resolveUserFullName(data.first_name, data.last_name);
    const signature = mailboxConnected
      ? data.signature
      : buildDisconnectedMailboxSignature(fullName);

    try {
      await updateSettions({
        first_name: data.first_name,
        last_name: data.last_name,
        mobile_number: data.mobile_number,
        signature,
        service_description: data.service_description,
      }).unwrap();
      toast.success("Profile updated successfully");
    } catch (error) {
      console.error("Error updating profile:", error);
      toast.error("Failed to update profile");
    }
  };

  const handleProfilePictureChange = (file: File | null) => {
    setProfilePicture(file);
  };

  const handleMobileChange = (value: string, countryCode?: string) => {
    setValue("mobile_number", value, {
      shouldDirty: true,
      shouldValidate: true,
    });
    if (countryCode) {
      setValue("country_code", countryCode, {
        shouldDirty: true,
      });
    }
  };

  if (isFetching && !user) {
    return <FullScreenLoader />;
  }

  return (
    <Row>
      <Col>
        <Card
          className="profile-page-shell"
          style={{
            border: "none",
            borderLeft: "1px solid rgba(25, 34, 46, 0.08)",
            borderRadius: 0,
            minHeight: "100vh",
            backgroundColor: "transparent",
          }}
        >
          <Card.Body style={{ padding: 0, backgroundColor: "transparent" }}>
            <FormProvider {...methods}>
              <div
                className="sticky-filter-bar bg-white"
                style={{
                  position: "sticky",
                  top: "50px",
                  zIndex: 100,
                  height: "50px",
                  marginBottom: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: "20px",
                  padding: "10px 20px",
                  lineHeight: "24px",
                  borderBottom: "1px solid #eef0f2",
                }}
              >
                <p
                  style={{
                    margin: 0,
                    fontSize: "14px",
                    color: "#6c757d",
                    fontWeight: 600,
                  }}
                >
                  Update your account details, email signature, and mailbox
                  connection.
                </p>
                <PrimarySmallButton
                  onClick={() => void handleSubmit(onSubmit)()}
                  disabled={isSaving || !isDirty}
                  icon={<IoSaveOutline />}
                >
                  {isSaving ? "Saving…" : "Save changes"}
                </PrimarySmallButton>
              </div>

              <div className="profile-page">
                <div className="profile-page__card">
                  <div className="profile-page__layout">
                    <ProfileIdentityCard
                      user={user}
                      mobileNumber={mobileNumber}
                      onPictureChange={handleProfilePictureChange}
                    />

                    <form
                      onSubmit={handleSubmit(onSubmit)}
                      className="profile-form"
                    >
                      <section className="profile-form__section">
                        <div className="profile-form__section-head">
                          <h2>Personal information</h2>
                          <p>Name and contact details for your account.</p>
                        </div>
                        <Row className="g-3">
                          <Col md={6}>
                            <FormInput
                              label="First Name"
                              name="first_name"
                              register={register}
                              errors={errors}
                              className="form-control"
                            />
                          </Col>
                          <Col md={6}>
                            <FormInput
                              label="Last Name"
                              name="last_name"
                              register={register}
                              errors={errors}
                              className="form-control"
                            />
                          </Col>
                          <Col md={6}>
                            <FormInput
                              label="Email"
                              name="email"
                              register={register}
                              errors={errors}
                              className="form-control"
                              disabled
                            />
                          </Col>
                          <Col md={6}>
                            <PhoneInput
                              label="Mobile"
                              name="mobile_number"
                              value={mobileNumber}
                              onChange={handleMobileChange}
                              errors={errors}
                              className="form-control"
                            />
                          </Col>
                        </Row>
                      </section>

                      <section className="profile-form__section">
                        <div className="profile-form__section-head">
                          <h2>About &amp; signature</h2>
                          <p>Shown in emails and on your public profile.</p>
                        </div>
                        <FormInput
                          type="textarea"
                          rows="4"
                          label="About Us"
                          name="service_description"
                          register={register}
                          errors={errors}
                          className="form-control"
                        />
                        <div className="mt-3">
                          <label className="form-label">Signature</label>
                          {!mailboxConnected && (
                            <p className="text-muted small mb-2">
                              Connect your mailbox to customize your email
                              signature.
                            </p>
                          )}
                          <Signature
                            signature={signatureForDisplay}
                            isReadOnly={!mailboxConnected}
                            isBorderTop={true}
                            onChange={
                              mailboxConnected
                                ? (html) =>
                                    setValue("signature", html, {
                                      shouldValidate: true,
                                      shouldDirty: true,
                                    })
                                : undefined
                            }
                          />
                        </div>
                      </section>

                      <section className="profile-form__section profile-form__section--last">
                        <MailboxConnectButtons
                          variant="profile"
                          mailboxType={user?.mailbox_type}
                          mailboxScope={user?.scope}
                          onConnectSuccess={() => void refetch()}
                        />
                      </section>
                    </form>
                  </div>
                </div>
              </div>
            </FormProvider>
          </Card.Body>
        </Card>
      </Col>
    </Row>
  );
};

const Profile = () => (
  <GoogleOAuthProvider clientId={googleClientId}>
    <ProfileContent />
  </GoogleOAuthProvider>
);

export default Profile;
